import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { BakimBandi } from "./BakimBandi";
import { cn, Ikon, marka, type IkonAdi } from "@koclukpanel/ui";

/**
 * ÖĞRENCİ KABUĞU — sürüm 9b (UNİKOÇ görsel dili, Emre kararı 07.10.2026)
 *
 * ================================================================
 * NEDEN YENİDEN DÜZENLENDİ
 * ================================================================
 * Emre: "öğrenci paneline yeni eklediğimiz özellikler aşırı kötü ve menü
 * çok karışık görünüyor." Ölçülen sebep: mağaza, çark, liderlik, davet ve
 * siparişler `Gelişim → Ödüller` altında İKİNCİ bir seçici satırına
 * gömülüydü; mesajlar, bildirimler, takvim, talepler ve KVKK ise
 * `Hesabım` listesinde. Öğrencinin müşterinin listesindeki altı işi
 * (Program · Denemeler · Konu Takibi · Analiz · Mağaza · Çark) bulmak
 * için iki seviye seçici gezmesi gerekiyordu.
 *
 * ================================================================
 * YENİ DÜZEN — TELEFON (öncelik, tek el)
 * ================================================================
 *   alt şerit 5:  Bugün · Program · Denemeler · Analiz · Ödüller
 *   üst çubuk:    marka · Mesajlar (balon) · Bildirimler (zil) · profil menüsü
 *   profil menüsü: Hesabım · Takvim · Program isteği · İstek ve yardım ·
 *                  Onaylarım · KVKK · Güvenlik · Nasıl kullanılır ·
 *                  (Canlı çalışma izleme) · (Çocuğumun sayfası) · Çıkış
 *
 * Beşte kalma gerekçesi değişmedi: 390 px'te altı girdi 65 px'e düşüyor.
 * "Hesabım" alt şeritten ÇIKTI, çünkü yılda birkaç kez açılan işler en
 * değerli dokunma alanını tutuyordu; yerine müşterinin istediği Ödüller
 * geldi. Hesabım'ın içeriği silinmedi: profil menüsünde ve listede duruyor.
 *
 * ================================================================
 * MASAÜSTÜ (≥1024 px) — UNİKOÇ yan menüsü
 * ================================================================
 * 260 px mor degrade, profil bloğu, aynı gruplama: ana işler, Analiz'in
 * altında Konu Takibi ve Hata Defterim, ayrı "ÖDÜL MERKEZİ" bölümü,
 * en altta Profilim ve Çıkış Yap (ONERI §5.4: çıkış menünün en altında).
 * 768 px tablette alt şerit kalıyor: dokunmatik ekranda başparmak hâlâ
 * altta, fare yok.
 */

/** Alt şeritteki beş ana bölüm. */
export type OgrenciSekmesi = "bugun" | "program" | "denemeler" | "analiz" | "oduller" | "mentorum";

/** Analiz bölümünün görünümleri (Gelisim.tsx). */
export type AnalizHedefi = "analiz" | "konular" | "hata";
/** Ödül Merkezi: merkez ("oduller" = kartlar + Puanlarım) ve alt ekranlar (Oduller.tsx). */
export type OdulHedefi = "oduller" | "magaza" | "cark" | "liderlik" | "davet" | "siparislerim";
/** Hesabım alt ekranları (Hesabim.tsx `AltEkran`). */
export type HesapHedefi =
  | "hesap"
  | "mesaj"
  | "bildirim"
  | "gorsel"
  | "degisiklik"
  | "talep"
  | "onay"
  | "kvkk"
  | "guvenlik"
  | "yardim"
  | "canliIzleme";

/**
 * Gidilebilecek HER ekran tek bir adla. Gezinti üç yerden (alt şerit, yan
 * menü, profil menüsü) aynı adı kullanıyor; bir ekranın iki yoldan farklı
 * bir duruma açılması bu yüzden mümkün değil.
 */
export type OgrenciHedefi =
  | "bugun"
  | "program"
  | "takvim"
  | "denemeler"
  | "mentorum"
  | AnalizHedefi
  | OdulHedefi
  | HesapHedefi;

/** Hedefin alt şeritteki bölümü; profil menüsü ekranları hiçbirine ait değil. */
export function hedefBolumu(h: OgrenciHedefi): OgrenciSekmesi | null {
  switch (h) {
    case "mentorum":
    case "bugun":
    case "program":
    case "denemeler":
      return h;
    case "analiz":
    case "konular":
    case "hata":
      return "analiz";
    case "oduller":
    case "magaza":
    case "cark":
    case "liderlik":
    case "davet":
    case "siparislerim":
      return "oduller";
    default:
      return null;
  }
}

interface Girdi<A extends string = OgrenciHedefi> {
  anahtar: A;
  etiket: string;
  ikon: IkonAdi;
}

/**
 * Alt şerit — beş girdi. İKONLAR BİRBİRİNDEN AYRI: aynı ikonu iki kavrama
 * vermek iki ekranı görsel olarak birbirine karıştırır.
 */
export const OGRENCI_GIRDILERI: readonly Girdi<OgrenciSekmesi>[] = [
  { anahtar: "bugun", etiket: "Bugün", ikon: "bugun" },
  { anahtar: "program", etiket: "Program", ikon: "program" },
  { anahtar: "denemeler", etiket: "Denemeler", ikon: "deneme" },
  { anahtar: "analiz", etiket: "Analiz", ikon: "analiz" },
  { anahtar: "oduller", etiket: "Ödüller", ikon: "hediye" },
];

interface YanGirdi extends Girdi {
  /** Bir üst girdinin altında girintili (UNİKOÇ alt satırı). */
  alt?: boolean;
  /** Okunmamış sayısının hangi kutudan geldiği. */
  sayac?: "mesaj" | "bildirim";
}

/** Masaüstü yan menüsü — ana bölüm. Sırası alt şeritle aynı; alt girdiler bölümün altında. */
export const YAN_MENU_ANA: readonly YanGirdi[] = [
  { anahtar: "bugun", etiket: "Bugün", ikon: "bugun" },
  { anahtar: "program", etiket: "Haftalık Program", ikon: "program" },
  { anahtar: "takvim", etiket: "Takvimim", ikon: "takvim", alt: true },
  { anahtar: "denemeler", etiket: "Deneme Sınavları", ikon: "deneme" },
  { anahtar: "analiz", etiket: "Analiz ve Raporlama", ikon: "analiz" },
  { anahtar: "konular", etiket: "Konu Takibi", ikon: "mufredat", alt: true },
  { anahtar: "hata", etiket: "Hata Defterim", ikon: "defter", alt: true },
  { anahtar: "mesaj", etiket: "Mesajlar", ikon: "sohbet", sayac: "mesaj" },
  { anahtar: "bildirim", etiket: "Bildirimlerim", ikon: "bildirim", sayac: "bildirim" },
];

/** "ÖDÜL MERKEZİ" bölümü — UNİKOÇ'taki ayrı başlıklı bölüm. */
export const YAN_MENU_ODUL: readonly YanGirdi[] = [
  // "Puanlarım" Ödül Merkezi'nin kendisi (kartlar + bakiye) — ayrı bir ekran değil.
  { anahtar: "oduller", etiket: "Puanlarım", ikon: "hedef" },
  { anahtar: "cark", etiket: "Bonus Çarkı", ikon: "cark" },
  { anahtar: "magaza", etiket: "Mağaza", ikon: "canta" },
  { anahtar: "siparislerim", etiket: "Sipariş Geçmişim", ikon: "arsiv" },
  { anahtar: "liderlik", etiket: "Liderlik", ikon: "kupa" },
  { anahtar: "davet", etiket: "Davet Ettiklerim", ikon: "ogrenci" },
];

/**
 * Profil menüsü — yılda birkaç kez açılan işler. Hepsi Hesabım'ın alt
 * ekranları (ya da Takvim); Hesabım listesi de onları göstermeye devam
 * ediyor, yani buradan kalkan bir girdi ekranı kaybettirmez.
 */
export const PROFIL_MENUSU: readonly Girdi[] = [
  { anahtar: "hesap", etiket: "Hesabım", ikon: "hesap" },
  { anahtar: "takvim", etiket: "Takvimim", ikon: "takvim" },
  { anahtar: "degisiklik", etiket: "Program isteği", ikon: "duzenle" },
  { anahtar: "talep", etiket: "İstek ve yardım", ikon: "talep" },
  { anahtar: "onay", etiket: "Onaylarım", ikon: "onay" },
  { anahtar: "kvkk", etiket: "KVKK başvurularım", ikon: "kvkk" },
  { anahtar: "guvenlik", etiket: "Güvenlik", ikon: "guvenlik" },
  { anahtar: "yardim", etiket: "Nasıl kullanılır", ikon: "yardim" },
  { anahtar: "canliIzleme", etiket: "Canlı çalışma izleme", ikon: "canli" },
];

const MENTORUM_GIRDISI: Girdi<"mentorum"> = {
  anahtar: "mentorum",
  etiket: "Mentörüm Yanımda",
  ikon: "defter",
};

export interface OgrenciKabuguProps {
  mentorumAcik?: boolean;
  yalnizMentorum?: boolean;
  aktif: OgrenciHedefi;
  onSec: (hedef: OgrenciHedefi) => void;
  /** Üst çubuk ve yan menüdeki sayılı rozetler. */
  okunmamis?: { mesaj: number; bildirim: number };
  /** Profil bloğu ve avatar baş harfleri; bilinmiyorsa (çevrimdışı açılış) ikon. */
  ad?: string | null;
  /** F-LIV-07 — modül kapalıysa "Canlı çalışma izleme" girdisi hiç çizilmez. */
  canliAcik?: boolean;
  /** Hem öğrenci hem veli olan kişi için "Çocuğumun sayfası". */
  onVeliyeGec?: () => void;
  /** Çıkış — profil menüsünün ve yan menünün en altında. Verilmezse çizilmez. */
  onCikis?: () => void;
  /** Üst çubuğun altında, her ekranda duran şeritler (bağlantı, sürüm). */
  ustBilgi?: ReactNode;
  children: ReactNode;
}

export function OgrenciKabugu({
  aktif,
  onSec,
  mentorumAcik = false,
  yalnizMentorum = false,
  okunmamis,
  ad,
  canliAcik = false,
  onVeliyeGec,
  onCikis,
  ustBilgi,
  children,
}: OgrenciKabuguProps) {
  const bolum = hedefBolumu(aktif);
  // F-MNT-03 — yalnız Mentörüm izniyle gelen hesapta diğer menü girişleri çizilmez.
  const anaGirdiler = yalnizMentorum ? [] : YAN_MENU_ANA;
  const altGirdiler = yalnizMentorum ? [] : OGRENCI_GIRDILERI;
  const profilGirdileri = yalnizMentorum ? [] : PROFIL_MENUSU;

  const sayi = (s?: "mesaj" | "bildirim"): number => (s ? (okunmamis?.[s] ?? 0) : 0);
  const yanSatir = (g: YanGirdi) => (
    <li key={g.anahtar}>
      <button
        type="button"
        onClick={() => onSec(g.anahtar)}
        aria-current={g.anahtar === aktif ? "page" : undefined}
        className={cn(
          "text-menu-metin flex w-full items-center gap-2 rounded-[0.875rem] text-left leading-snug",
          "ease-cikis transition-colors duration-(--sure-anlik)",
          g.alt
            ? "min-h-dokunma ml-9 w-[calc(100%-2.25rem)] py-1.5 pr-3 pl-[1.125rem] text-[0.875rem]"
            : "min-h-[2.875rem] py-1.5 pr-3.5 pl-[1.125rem] text-[0.9375rem]",
          g.anahtar === aktif
            ? "bg-menu-aktif ring-menu-halka ring-1 ring-inset"
            : "hover:bg-menu-uzeri",
        )}
      >
        <Ikon ad={g.ikon} className={g.alt ? "size-[1.125rem]" : "size-5"} />
        <span className="min-w-0 flex-1">{g.etiket}</span>
        {sayi(g.sayac) > 0 && <Sayac adet={sayi(g.sayac)} />}
      </button>
    </li>
  );

  return (
    /*
      `ogrenci-yuzeyi` — UNİKOÇ GÖRÜNÜMÜNÜN KAPSAMI (stil.css). Renk, köşe,
      gölge ve yazı tipi bu sınıfın ALTINDA yeniden tanımlı; GİRİŞ EKRANI bu
      kabuğun dışında çizildiği için eski değerlerle kalıyor (karar: giriş
      sayfasına dokunulmaz).
    */
    <div className="ogrenci-yuzeyi text-notr-900 flex min-h-screen flex-col bg-(image:--gradyan-unk-zemin) bg-fixed">
      {/* BAKIM BANDI EN ÜSTTE — F-SEC-06. Engelleme sunucuda (503); bant yalnızca nedenini söylüyor. */}
      <BakimBandi />

      <a
        href="#icerik"
        className="focus:rounded-alan focus:bg-notr-0 sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:px-4 focus:py-2 focus:shadow"
      >
        İçeriğe geç
      </a>

      {/*
        ================================================================
        MASAÜSTÜ: 260 px MOR YAN MENÜ (UNİKOÇ)
        ================================================================
        `fixed`: uzun sayfada mor zemin ekran yüksekliğinde kesilmesin
        (9a'da `sticky` ile ölçülen kusur). İçerik sütunu menü kadar soldan
        boşluk bırakıyor (`lg:pl-[260px]`).

        GEZİNTİ İŞARETİ `nav`DA: `aside` ekran okuyucuda "tamamlayıcı bölge"
        diye duyuruluyor; iki menüden yalnız biri görünür (öteki
        `display:none`), erişilebilirlik ağacında tek "Ana menü" kalıyor.
      */}
      <nav
        aria-label="Ana menü"
        className="text-menu-metin yan-kaydirma fixed inset-y-0 left-0 z-30 hidden w-[260px] flex-col overflow-y-auto bg-(image:--gradyan-unk-menu) lg:flex"
      >
        <div className="border-menu-cizgi flex min-h-[3.875rem] shrink-0 items-center gap-3 border-b px-5">
          <span className="truncate text-[1.375rem] leading-none font-extrabold tracking-[0.02em] uppercase">
            {marka.kisaAd}
          </span>
        </div>
        <div className="border-menu-cizgi flex shrink-0 flex-col items-center border-b px-3 pt-5 pb-4 text-center">
          <Avatar ad={ad} buyuk />
          {ad && <p className="mt-3 w-full truncate text-[0.9375rem]">{ad}</p>}
          <p className="mt-1 text-xs font-bold tracking-[0.04em]">ÖĞRENCİ</p>
        </div>
        <ul className="flex flex-col gap-0.5 px-2.5 pt-3">
          {anaGirdiler.map(yanSatir)}
          {mentorumAcik && yanSatir(MENTORUM_GIRDISI)}
        </ul>
        {!yalnizMentorum && (
          <div className="border-menu-cizgi mx-2.5 mt-2 border-t pt-2">
            <p
              id="yan-odul-merkezi"
              className="flex min-h-[2.875rem] items-center gap-2 pr-3.5 pl-[1.125rem] text-[0.9375rem] font-bold"
            >
              <Ikon ad="hediye" className="size-5" />
              ÖDÜL MERKEZİ
            </p>
            <ul aria-labelledby="yan-odul-merkezi" className="flex flex-col gap-0.5">
              {YAN_MENU_ODUL.map(yanSatir)}
            </ul>
          </div>
        )}
        <ul className="border-menu-cizgi mx-2.5 mt-2 flex flex-col gap-0.5 border-t pt-2 pb-4">
          {!yalnizMentorum && yanSatir({ anahtar: "hesap", etiket: "Profilim", ikon: "profil" })}
          {onCikis && (
            <li>
              <button
                type="button"
                onClick={onCikis}
                className="text-menu-metin hover:bg-menu-uzeri flex min-h-[2.875rem] w-full items-center gap-2 rounded-[0.875rem] py-1.5 pr-3.5 pl-[1.125rem] text-left text-[0.9375rem] transition-colors"
              >
                <Ikon ad="cikis" className="size-5" />
                Çıkış Yap
              </button>
            </li>
          )}
        </ul>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col lg:pl-[260px]">
        {/*
          ================================================================
          ÜST ÇUBUK — TEK DOM, İKİ GÖRÜNÜM
          ================================================================
          Telefonda menü renginde (mor degrade) yapışkan şerit; masaüstünde
          UNİKOÇ'taki gibi zeminde sağ üstte yüzen düğmeler. Tek eleman, çünkü
          iki kopya aynı adlı iki "Bildirimler" düğmesi üretirdi.
        */}
        <header className="text-menu-metin lg:text-metin-koyu sticky top-0 z-20 bg-(image:--gradyan-unk-menu) lg:static lg:bg-none">
          <div className="mx-auto flex w-full max-w-[1200px] items-center gap-1.5 px-3 py-2 lg:justify-end lg:px-10 lg:pt-4">
            <span className="min-w-0 flex-1 truncate text-lg font-extrabold tracking-[0.02em] uppercase lg:hidden">
              {marka.kisaAd}
            </span>
            {!yalnizMentorum && <MesajDugmesi adet={sayi("mesaj")} onAc={() => onSec("mesaj")} />}
            {!yalnizMentorum && (
              <ZilDugmesi adet={sayi("bildirim")} onAc={() => onSec("bildirim")} />
            )}
            <ProfilMenusu
              girdiler={profilGirdileri}
              mentorumAcik={mentorumAcik && !yalnizMentorum}
              ad={ad}
              aktif={aktif}
              canliAcik={canliAcik}
              onSec={onSec}
              onVeliyeGec={onVeliyeGec}
              onCikis={onCikis}
            />
          </div>
        </header>

        {ustBilgi && <div className="mx-auto w-full max-w-[1200px] px-4 lg:px-10">{ustBilgi}</div>}

        {/*
          OKUMA SÜTUNU SINIRLI: telefonda tam genişlik, tablette 720 px,
          masaüstünde 1200 px (program kartları üç sütun). Sınırsız genişlik
          satırları okunamayacak kadar uzatırdı.
        */}
        <main
          id="icerik"
          tabIndex={-1}
          className="mx-auto flex w-full max-w-[720px] min-w-0 flex-1 flex-col outline-none lg:max-w-[1200px] lg:px-6 lg:pb-12"
        >
          {children}
        </main>

        {/*
          ================================================================
          TELEFON + TABLET: ALT ŞERİT
          ================================================================
          Başparmak ekranın altına ulaşıyor. `sticky`: içerik kaysa da yerinde
          duruyor ama akışta yer kaplıyor, son kart altına gizlenmiyor. Alttaki
          18 px telefonun ana ekran çubuğu için pay.
        */}
        <nav
          aria-label="Ana menü"
          className="border-mor-kenar bg-notr-0 sticky bottom-0 z-20 flex border-t px-1 pt-1.5 pb-[18px] shadow-[0_-6px_20px_rgb(80_60_140/0.08)] lg:hidden"
        >
          {(yalnizMentorum && mentorumAcik ? [MENTORUM_GIRDISI] : altGirdiler).map((g) => {
            const secili = g.anahtar === bolum;
            return (
              <button
                key={g.anahtar}
                type="button"
                onClick={() => onSec(g.anahtar)}
                aria-current={secili ? "page" : undefined}
                className={cn(
                  "flex min-h-[52px] min-w-0 flex-1 basis-0 flex-col items-center justify-center gap-1",
                  "rounded-alan text-xs whitespace-nowrap",
                  "ease-cikis transition-colors duration-(--sure-anlik)",
                  secili ? "text-mor-700 font-semibold" : "text-metin-ikincil",
                )}
              >
                <span
                  className={cn(
                    "grid h-7 w-12 place-items-center rounded-full transition-colors",
                    secili &&
                      "text-notr-0 bg-(image:--gradyan-unk-birincil) shadow-(--golge-unk-birincil)",
                  )}
                >
                  <Ikon ad={g.ikon} boyut="normal" />
                </span>
                <span className="truncate">{g.etiket}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

/** Baş harfler: "Elif Yılmaz" → "EY". Fotoğraf altyapısı kabukta yok; avatar baş harflerden. */
export function basHarfler(ad: string): string {
  const p = ad.trim().split(/\s+/).filter(Boolean);
  const harf = (s?: string) => (s ? s[0]!.toLocaleUpperCase("tr-TR") : "");
  return harf(p[0]) + (p.length > 1 ? harf(p[p.length - 1]) : "");
}

function Avatar({ ad, buyuk = false }: { ad?: string | null; buyuk?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid shrink-0 place-items-center rounded-full font-bold",
        buyuk
          ? "bg-menu-aktif ring-notr-0/85 size-[5.5rem] text-2xl ring-[3px]"
          : "text-notr-0 size-8 bg-(image:--gradyan-unk-birincil) text-xs",
      )}
    >
      {ad ? basHarfler(ad) : <Ikon ad="profil" className={buyuk ? "size-8" : "size-4"} />}
    </span>
  );
}

/**
 * Okunmamış sayacı. Sayı `aria-label` ile de yazılı: rozetin görsel yeri
 * ekran okuyucuda bir şey ifade etmiyor. 99 üstü kırpılıyor.
 */
function Sayac({ adet }: { adet: number }) {
  return (
    <span
      aria-label={`${adet} okunmamış`}
      className="bg-hata-700 text-notr-0 inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] leading-[18px] font-semibold"
    >
      {adet > 99 ? "99+" : adet}
    </span>
  );
}

/*
  Üst çubuk düğmesi: telefonda mor şeridin üstünde yarı saydam beyaz kare,
  masaüstünde UNİKOÇ'taki beyaz kare (zil, tarama-1 §2.5). Dokunma hedefi
  44 px: üst köşe tek elde en uzak nokta.
*/
const UST_DUGME = cn(
  "size-dokunma relative inline-grid shrink-0 place-items-center rounded-[0.625rem] transition-colors",
  "bg-menu-aktif ring-menu-halka hover:bg-menu-uzeri ring-1 ring-inset",
  "lg:bg-notr-0 lg:ring-mor-kenar lg:hover:bg-mor-50 lg:shadow-(--golge-unk-kart)",
);

function UstRozet({ adet }: { adet: number }) {
  if (adet <= 0) return null;
  return (
    <span
      aria-hidden="true"
      className="bg-hata-700 text-notr-0 ring-notr-0 absolute -top-1 -right-1 inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] leading-[18px] font-semibold ring-2"
    >
      {adet > 99 ? "99+" : adet}
    </span>
  );
}

/**
 * Üst çubuktaki zil — F-NTF-09. Sayı YALNIZ bildirim; basınca doğrudan
 * bildirim kutusunu açıyor. Sayı `aria-label`da da yazılı.
 */
export function ZilDugmesi({ adet, onAc }: { adet: number; onAc: () => void }) {
  return (
    <button
      type="button"
      onClick={onAc}
      aria-label={adet > 0 ? `Bildirimler, ${adet > 99 ? "99+" : adet} okunmamış` : "Bildirimler"}
      className={UST_DUGME}
    >
      <Ikon ad="bildirim" boyut="normal" />
      <UstRozet adet={adet} />
    </button>
  );
}

/**
 * Mesaj balonu — F-MSG. Mesajlar 9b'ye kadar Hesabım listesinin içindeydi ve
 * şeritteki "Hesabım" rozeti mesajla bildirimin TOPLAMINI gösteriyordu; hangi
 * kutunun dolu olduğu söylenmiyordu. Artık her kutunun kendi düğmesi ve kendi
 * sayısı var.
 */
export function MesajDugmesi({ adet, onAc }: { adet: number; onAc: () => void }) {
  return (
    <button
      type="button"
      onClick={onAc}
      aria-label={adet > 0 ? `Mesajlar, ${adet > 99 ? "99+" : adet} okunmamış` : "Mesajlar"}
      className={UST_DUGME}
    >
      <Ikon ad="sohbet" boyut="normal" />
      <UstRozet adet={adet} />
    </button>
  );
}

/**
 * PROFİL MENÜSÜ — açılır liste (WAI-ARIA menü düğmesi kalıbı).
 *
 * Esc ve dışarı tıklama kapatıyor; açılınca odak ilk girdiye gidiyor, kapanınca
 * düğmeye dönüyor — klavyeyle gezen kişi menüde kaybolmasın.
 */
function ProfilMenusu({
  girdiler,
  mentorumAcik,
  ad,
  aktif,
  canliAcik,
  onSec,
  onVeliyeGec,
  onCikis,
}: {
  girdiler: readonly Girdi[];
  mentorumAcik: boolean;
  ad?: string | null;
  aktif: OgrenciHedefi;
  canliAcik: boolean;
  onSec: (h: OgrenciHedefi) => void;
  onVeliyeGec?: () => void;
  onCikis?: () => void;
}) {
  const [acik, setAcik] = useState(false);
  const kok = useRef<HTMLDivElement>(null);
  const dugme = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLUListElement>(null);
  const kimlik = useId();

  useEffect(() => {
    if (!acik) return;
    liste.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
    const disari = (e: MouseEvent) => {
      if (kok.current && !kok.current.contains(e.target as Node)) setAcik(false);
    };
    const tus = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setAcik(false);
        dugme.current?.focus();
      }
    };
    document.addEventListener("mousedown", disari);
    document.addEventListener("keydown", tus);
    return () => {
      document.removeEventListener("mousedown", disari);
      document.removeEventListener("keydown", tus);
    };
  }, [acik]);

  const sec = (f: () => void) => {
    setAcik(false);
    f();
    /*
      Seçilen girdi menüyle birlikte DOM'dan kalkıyor; odak BODY'ye düşerse klavye ve ekran
      okuyucu kullanıcısı sayfanın başına atılır. Odak, "İçeriğe geç" bağlantısının hedefi olan
      ana içeriğe taşınır (açılan ekran orada çizilir; Escape yolu ayrıca düğmeye döner).
    */
    document.getElementById("icerik")?.focus();
  };
  const okla = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const ogeler = [...(liste.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [])];
    const i = ogeler.indexOf(document.activeElement as HTMLElement);
    const sonraki =
      e.key === "ArrowDown" ? (i + 1) % ogeler.length : (i - 1 + ogeler.length) % ogeler.length;
    ogeler[sonraki]?.focus();
  };
  const profilSecili = hedefBolumu(aktif) === null && aktif !== "mesaj" && aktif !== "bildirim";
  const satirSinifi =
    "min-h-dokunma text-metin-koyu hover:bg-mor-50 focus:bg-mor-50 flex w-full items-center gap-3 rounded-xl px-3 text-left text-sm outline-none";

  return (
    <div ref={kok} className="relative">
      <button
        ref={dugme}
        type="button"
        aria-haspopup="menu"
        aria-expanded={acik}
        aria-controls={acik ? kimlik : undefined}
        aria-label="Profil menüsü"
        onClick={() => setAcik((a) => !a)}
        className={cn(
          UST_DUGME,
          "lg:inline-flex lg:w-auto lg:gap-2 lg:px-2",
          profilSecili && "lg:ring-mor-500",
        )}
      >
        <Avatar ad={ad} />
        {ad && (
          <span className="text-metin-koyu hidden max-w-40 truncate text-sm font-semibold lg:inline">
            {ad}
          </span>
        )}
      </button>
      {acik && (
        <ul
          id={kimlik}
          ref={liste}
          role="menu"
          aria-label="Profil menüsü"
          onKeyDown={okla}
          className="bg-notr-0 border-mor-kenar absolute top-full right-0 z-40 mt-2 flex max-h-[75vh] w-64 flex-col gap-0.5 overflow-y-auto rounded-2xl border p-2 shadow-(--golge-unk-kart)"
        >
          {(mentorumAcik ? [MENTORUM_GIRDISI, ...girdiler] : girdiler)
            .filter((g) => g.anahtar !== "canliIzleme" || canliAcik)
            .map((g) => (
              <li key={g.anahtar} role="none">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => sec(() => onSec(g.anahtar))}
                  className={cn(satirSinifi, g.anahtar === aktif && "bg-mor-50 font-semibold")}
                >
                  <Ikon ad={g.ikon} className="text-mor-500 size-5" />
                  {g.etiket}
                </button>
              </li>
            ))}
          {onVeliyeGec && (
            <li role="none" className="border-mor-kenar mt-1 border-t pt-1">
              <button
                type="button"
                role="menuitem"
                onClick={() => sec(onVeliyeGec)}
                className={satirSinifi}
              >
                <Ikon ad="ogrenci" className="text-mor-500 size-5" />
                Çocuğumun sayfasına geç
              </button>
            </li>
          )}
          {onCikis && (
            <li role="none" className="border-mor-kenar mt-1 border-t pt-1">
              <button
                type="button"
                role="menuitem"
                onClick={() => sec(onCikis)}
                className={satirSinifi}
              >
                <Ikon ad="cikis" className="text-eksik-metin size-5" />
                Çıkış yap
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
